/**
 * ContactOverviewPage
 *
 * Admin view of a contact's home screen — mirrors the investor app HomeScreen.
 * Sections:
 *   1. Contact header + Edit link
 *   2. Investment Overview hero card  (total committed, ROE / IRR / Money Multiple)
 *   3. Next 30 days projection        (capital calls & distributions)
 *   4. Notes
 *   5. Upcoming opportunities (active EOIs)
 *
 * Route: /contacts/:id/overview
 * Opened when admin clicks "Home" context chip in the Fides Chat panel.
 */

import AssignmentIcon from '@mui/icons-material/Assignment';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ContactNotesSection from './ContactOverview/ContactNotesSection';
import ContactOverviewHeader from './ContactOverview/ContactOverviewHeader';
import ContactOverviewHeroCard from './ContactOverview/ContactOverviewHeroCard';
import ContactOverviewInvestmentCard from './ContactOverview/ContactOverviewInvestmentCard';
import ContactOverviewProjectionRow from './ContactOverview/ContactOverviewProjectionRow';
import ContactOverviewSectionCard from './ContactOverview/ContactOverviewSectionCard';
import { useContactOverviewData } from './ContactOverview/useContactOverviewData';

export default function ContactOverviewPage() {
  const { id: contactId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const {
    contact,
    contactLoading,
    contactError,
    metrics,
    metricsLoading,
    committedInvestment,
    completeFunds,
    includeClosedFunds,
    setIncludeClosedFunds,
    eventsLoading,
    show90,
    setShow90,
    displayEvents,
    investmentsLoading,
    eoiInvestors,
    portfolioInvestors,
  } = useContactOverviewData(contactId);

  if (contactLoading) {
    return (
      <Box display="flex" justifyContent="center" mt={6}>
        <CircularProgress />
      </Box>
    );
  }
  if (contactError || !contact) {
    return (
      <Box p={3}>
        <Alert severity="error">{contactError ?? 'Contact not found'}</Alert>
        <Button sx={{ mt: 2 }} onClick={() => navigate('/contacts')}>
          Back to Contacts
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 860, mx: 'auto', px: { xs: 2, md: 3 }, py: 3 }}>
      <ContactOverviewHeader contact={contact} onEdit={() => navigate(`/contacts/${contactId}`)} />

      <ContactOverviewHeroCard
        metrics={metrics}
        loading={metricsLoading}
        committedInvestment={committedInvestment}
        completeFunds={completeFunds}
        includeClosedFunds={includeClosedFunds}
        onToggleClosedFunds={setIncludeClosedFunds}
      />

      <ContactOverviewSectionCard
        title={show90 ? 'Next 90 days projection' : 'Next 30 days projection'}
        icon={<TrendingUpIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
        loading={eventsLoading}
      >
        {!eventsLoading && displayEvents.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No upcoming events in this window.
          </Typography>
        )}
        {displayEvents.map((ev) => (
          <ContactOverviewProjectionRow key={ev.eventExternalId} event={ev} />
        ))}
        <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
          <Button
            size="small"
            variant={!show90 ? 'contained' : 'outlined'}
            onClick={() => setShow90(false)}
            sx={{ textTransform: 'none' }}
          >
            30 days
          </Button>
          <Button
            size="small"
            variant={show90 ? 'contained' : 'outlined'}
            onClick={() => setShow90(true)}
            sx={{ textTransform: 'none' }}
          >
            90 days
          </Button>
        </Box>
      </ContactOverviewSectionCard>

      <ContactOverviewSectionCard
        title="Notes"
        icon={<AssignmentIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
      >
        <ContactNotesSection contactExternalId={contactId!} />
      </ContactOverviewSectionCard>

      <ContactOverviewSectionCard
        title="New Opportunities — Expression of Interest"
        icon={<AssignmentIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
        loading={investmentsLoading}
      >
        {!investmentsLoading && eoiInvestors.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No active expressions of interest.
          </Typography>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {eoiInvestors.map((inv) => (
            <ContactOverviewInvestmentCard key={inv.id} investor={inv} navigate={navigate} />
          ))}
        </Box>
      </ContactOverviewSectionCard>

      <ContactOverviewSectionCard
        title="My Funds"
        icon={<TrendingUpIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
        loading={investmentsLoading}
      >
        {!investmentsLoading && portfolioInvestors.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No committed investments.
          </Typography>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {portfolioInvestors.map((inv) => (
            <ContactOverviewInvestmentCard key={inv.id} investor={inv} navigate={navigate} />
          ))}
        </Box>
      </ContactOverviewSectionCard>
    </Box>
  );
}
